import { useRef, useCallback, useEffect } from 'react';
import { isFileAcceptable } from '../../lib/fileUtils';
import API from '../../API';

export function useImageOptimizer() {
    const workerRef = useRef(null);
    const pendingPromises = useRef({});

    const { optimizeGif } = API;

    useEffect(() => {
        if (!workerRef.current) {
            workerRef.current = new Worker(new URL('../../workers/imageOptimizer.worker.js', import.meta.url));

            workerRef.current.onmessage = (event) => {
                const { processedImages, error, id, type } = event.data || {};

                if (error) {
                    const originalPromise = Object.values(pendingPromises.current).find(p => p.id === id);
                    if (originalPromise) {
                        originalPromise.reject(new Error(error));
                        delete pendingPromises.current[originalPromise.fileName];
                    };
                    return;
                };

                if (processedImages) {
                    const originalPromise = Object.values(pendingPromises.current).find(p => p.id === id);

                    if (originalPromise) {
                        const files = processedImages.map(img => {
                            return new File([img.buffer], img.metadata.name, {
                                type: img.metadata.type,
                                lastModified: img.metadata.lastModified
                            });
                        });

                        originalPromise.resolve({ files, id, type });

                        delete pendingPromises.current[originalPromise.fileName];
                    };
                    return;
                };

                const { buffer, metadata } = event.data || {};
                
                if (metadata?.name && pendingPromises.current[metadata?.name] && (!metadata.type === 'image/webp' || !metadata.type === 'pdf')) {
                    pendingPromises.current[metadata.name].reject(new Error('File type is wrong'));
                };
                
                const file = new File([buffer], metadata.name, {
                    type: metadata.type,
                    lastModified: metadata.lastModified
                });
                
                if (pendingPromises.current[file.name]) {
                    pendingPromises.current[file.name].resolve({ file, id });
                };
            };

            workerRef.current.onerror = (error) => {
                console.error('Web Worker error:', error);
                Object.values(pendingPromises.current).forEach(promise =>
                    promise.reject(new Error('Worker crashed or encountered an unhandled error'))
                );
                pendingPromises.current = {};
            };
        };

        return () => {
            if (workerRef.current) {
                workerRef.current.terminate();
                workerRef.current = null;
            };
        };
    }, []);

    const processFile = useCallback(async (file, acceptableFileTypes, id, type) => {
        if (!workerRef.current) {
            throw new Error('Image optimizer worker not initialized.');
        };

        if (!isFileAcceptable(file, acceptableFileTypes, 5000000)) {
            throw new Error('exceeds file size limit');
        };

        if (file.type === 'application/pdf') {
            if (type === 'logo') throw (new Error('incompatible file type'));
            return { files: [file], id };
        };

        if (file.type === 'image/gif') {
            if (type === 'logo') throw (new Error('incompatible file type'));

            try {
                const formData = new FormData();
                formData.append('file', file);
                const { data } = await optimizeGif(formData);

                return {
                    id,
                    isGif: true,
                    name: `${id}.webp`,
                    path: `temp_files/${id}.webp`,
                    url: data
                };
            } catch (err) {
                console.error(err);
                throw err;
            };
        };

        let arrayBuffer;
        try {
            arrayBuffer = await file.arrayBuffer();
        } catch (e) {
            console.error('Error creating ArrayBuffer from file:', e);
            throw new Error(`Failed to read file into ArrayBuffer: ${e.message}`);
        };

        const fileMetadata = {
            name: file.name,
            type: file.type,
            lastModified: file.lastModified || Date.now(),
        };

        return new Promise((resolve, reject) => {
            // Store the promise, along with a reference to the file name and id
            pendingPromises.current[file.name] = { resolve, reject, id, fileName: file.name };

            try {
                workerRef.current.postMessage({
                    buffer: arrayBuffer,
                    id,
                    metadata: fileMetadata,
                    type,
                }, [arrayBuffer]);
            } catch (e) {
                console.error('Error posting message to worker:', e);
                reject(e);
            };
        });
    }, []);

    return processFile;
};
